import { AlertTriangle, CheckCircle, RotateCcw, ScanLine, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// ============================================================================
// TIPOS
// ============================================================================

export type CheckInResultStatus = 'success' | 'already_checked_in' | 'error' | 'invalid';

export interface CheckInTicketData {
  ticketCode: string;
  customerName: string;
  seatIdentifier: string;
  checkedInAt?: string;
  checkedInBy?: string;
}

export interface CheckInResultModalProps {
  isOpen: boolean;
  status: CheckInResultStatus;
  message?: string;
  ticket?: CheckInTicketData;
  onClose: () => void;
  onScanNext: () => void;
  onRetry?: () => void;
  autoCloseTimeout?: number; // en milisegundos, 0 = sin auto-close
}

// ============================================================================
// CONFIGURACIÓN POR ESTADO
// ============================================================================

const statusConfig: Record<
  CheckInResultStatus,
  {
    title: string;
    subtitle: string;
    icon: typeof CheckCircle;
    iconBg: string;
    iconColor: string;
    ringColor: string;
    primaryBtnText: string;
    primaryBtnClass: string;
    showRetry: boolean;
  }
> = {
  success: {
    title: 'Check-in confirmado',
    subtitle: 'Acceso registrado correctamente.',
    icon: CheckCircle,
    iconBg: 'bg-green-500',
    iconColor: 'text-white',
    ringColor: 'ring-green-200',
    primaryBtnText: 'Escanear siguiente',
    primaryBtnClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
    showRetry: false,
  },
  already_checked_in: {
    title: 'Este ticket ya fue registrado',
    subtitle: 'Verifica si el asistente ya ingresó.',
    icon: AlertTriangle,
    iconBg: 'bg-amber-500',
    iconColor: 'text-white',
    ringColor: 'ring-amber-200',
    primaryBtnText: 'Escanear otro',
    primaryBtnClass: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    showRetry: false,
  },
  error: {
    title: 'Error al validar',
    subtitle: 'No se pudo procesar el ticket.',
    icon: XCircle,
    iconBg: 'bg-red-500',
    iconColor: 'text-white',
    ringColor: 'ring-red-200',
    primaryBtnText: 'Escanear otro',
    primaryBtnClass: 'bg-slate-600 hover:bg-slate-700 focus:ring-slate-500',
    showRetry: true,
  },
  invalid: {
    title: 'Código no válido',
    subtitle: 'Intenta de nuevo o ingresa el código manualmente.',
    icon: XCircle,
    iconBg: 'bg-red-500',
    iconColor: 'text-white',
    ringColor: 'ring-red-200',
    primaryBtnText: 'Escanear otro',
    primaryBtnClass: 'bg-slate-600 hover:bg-slate-700 focus:ring-slate-500',
    showRetry: true,
  },
};

// ============================================================================
// COMPONENTE
// ============================================================================

export default function CheckInResultModal({
  isOpen,
  status,
  message,
  ticket,
  onClose,
  onScanNext,
  onRetry,
  autoCloseTimeout = 0, // Por defecto, sin auto-close
}: CheckInResultModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const primaryBtnRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const config = statusConfig[status];
  const Icon = config.icon;

  // Focus trap y keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    // Focus en el botón primario al abrir
    setTimeout(() => {
      primaryBtnRef.current?.focus();
    }, 100);

    // Manejar Escape
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Prevenir scroll del body
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Auto-close timeout
  useEffect(() => {
    if (!isOpen || autoCloseTimeout <= 0) return;

    timeoutRef.current = setTimeout(() => {
      onClose();
    }, autoCloseTimeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, autoCloseTimeout, onClose]);

  // Cancelar timeout si el usuario interactúa
  const cancelTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleScanNext = useCallback(() => {
    cancelTimeout();
    onScanNext();
  }, [cancelTimeout, onScanNext]);

  const handleRetry = useCallback(() => {
    cancelTimeout();
    onRetry?.();
  }, [cancelTimeout, onRetry]);

  const handleClose = useCallback(() => {
    cancelTimeout();
    onClose();
  }, [cancelTimeout, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkin-modal-title"
      onClick={handleClose}
    >
      {/* Overlay con blur */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        aria-hidden="true"
      />

      {/* Modal Card - Bottom sheet en móvil, centrado en desktop */}
      <div
        ref={modalRef}
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl 
                   shadow-2xl transform transition-all
                   animate-slide-in-from-bottom sm:animate-zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar para móvil */}
        <div className="sm:hidden flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
        </div>

        {/* Botón cerrar */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 
                     hover:bg-slate-200 flex items-center justify-center
                     text-slate-500 hover:text-slate-700 transition-colors
                     focus:outline-none focus:ring-2 focus:ring-slate-400"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Contenido */}
        <div className="px-6 pt-6 pb-8 sm:pt-8">
          {/* Icono principal */}
          <div className="flex justify-center mb-6">
            <div
              className={`w-20 h-20 rounded-full ${config.iconBg} ring-8 ${config.ringColor}
                            flex items-center justify-center shadow-lg`}
            >
              <Icon className={`w-10 h-10 ${config.iconColor}`} strokeWidth={2.5} />
            </div>
          </div>

          {/* Título y subtítulo */}
          <div className="text-center mb-6">
            <h2
              id="checkin-modal-title"
              className="text-2xl font-bold text-slate-900 mb-2"
              aria-live="assertive"
            >
              {message || config.title}
            </h2>
            <p className="text-slate-600">{config.subtitle}</p>
          </div>

          {/* Datos del ticket (si hay) */}
          {ticket && (
            <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Cliente</span>
                <span className="font-semibold text-slate-900">{ticket.customerName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Asiento</span>
                <span className="font-semibold text-slate-900">
                  {ticket.seatIdentifier.replace(/^TABLE/i, 'MESA')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Código</span>
                <span className="font-mono text-sm text-slate-700">{ticket.ticketCode}</span>
              </div>
              {ticket.checkedInAt && (
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-sm text-slate-500">Registrado</span>
                  <span className="text-sm text-slate-600">
                    {new Date(ticket.checkedInAt).toLocaleString('es-MX', {
                      hour: '2-digit',
                      minute: '2-digit',
                      day: '2-digit',
                      month: 'short',
                    })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="space-y-3">
            {/* Botón primario */}
            <button
              ref={primaryBtnRef}
              onClick={handleScanNext}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white
                         flex items-center justify-center gap-2
                         transition-all duration-200 active:scale-[0.98]
                         focus:outline-none focus:ring-4 ${config.primaryBtnClass}`}
            >
              <ScanLine className="w-5 h-5" />
              {config.primaryBtnText}
            </button>

            {/* Botón reintentar (solo para errores) */}
            {config.showRetry && onRetry && (
              <button
                onClick={handleRetry}
                className="w-full py-3 px-6 rounded-xl font-medium text-slate-700
                           bg-slate-100 hover:bg-slate-200
                           flex items-center justify-center gap-2
                           transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <RotateCcw className="w-4 h-4" />
                Reintentar
              </button>
            )}

            {/* Botón cerrar secundario */}
            <button
              onClick={handleClose}
              className="w-full py-3 text-sm text-slate-500 hover:text-slate-700
                         transition-colors focus:outline-none"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Renderizar en portal para estar fuera del árbol del componente
  return createPortal(modalContent, document.body);
}
